/**
 * Live kart — viser alle båter i en regatta med nåværende posisjon (seilnummer)
 * og et fargelagt spor for de siste minuttene. Oppdateres hvert par sekund.
 *
 * Leaflet + Kartverkets sjøkart (Big-Tech-fritt). Default-kartlag kan byttes til
 * topografisk eller OpenStreetMap via lagvelgeren.
 *
 * Samme paneler som reprisen: løpet som liste og regattaklokka nede til venstre
 * (nedtelling med flagg, tid etter start, til siste båt er i mål), og båtlista viser
 * plass og tid for båtene som er i mål.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { api } from '../lib/api.ts';
import { colorForSail } from '../lib/trackColors.ts';
import { boatIcon } from '../lib/boatMarker.ts';
import { createCourseLayer, lopRader, type CourseLayer } from '../lib/courseLayer.ts';
import { syncTime } from '../lib/timesync.ts';
import { ferdigeSeil, maalgangerPerSeil, sailKey, sorterMedMaal } from '../lib/maalganger.ts';
import type { RaceRunStatus } from '../lib/startProcedure.ts';
import { formatElapsed } from '../components/FinishTiming.tsx';
import { LopListe, RegattaKlokke } from '../components/KartPaneler.tsx';

// Grimstad-området som standard utgangspunkt før båter dukker opp.
const DEFAULT_CENTER: [number, number] = [58.34, 8.59];

const KV_ATTR = '© Kartverket';

/** Klokka vises fra en time før start og til et halvt døgn etter. */
const KLOKKE_FRA_MS = 60 * 60_000;
const KLOKKE_TIL_MS = 12 * 60 * 60_000;

interface Layer {
  marker: L.Marker;
  line: L.Polyline;
}

/**
 * Regattaklokka på live-kartet: serverens tid (tidssynk) mot starttiden. Tikker selv,
 * så resten av siden ikke tegnes på nytt fire ganger i sekundet. `iMaalMs` = siste
 * båts tid når alle er i mål (eller regattaen er ferdig) — da står klokka stille.
 */
function LiveKlokke({
  startMs,
  offsetMs,
  status,
  iMaalMs,
  klasseflagg,
}: {
  startMs: number;
  offsetMs: number;
  status: RaceRunStatus;
  iMaalMs: number | null;
  klasseflagg: string[];
}) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, []);
  const msToStart = iMaalMs != null ? -iMaalMs : startMs - (now + offsetMs);
  if (iMaalMs == null && status !== 'finished' && (msToStart > KLOKKE_FRA_MS || msToStart < -KLOKKE_TIL_MS)) {
    return null;
  }
  return <RegattaKlokke msToStart={msToStart} iMaal={iMaalMs != null} klasseflagg={klasseflagg} status={status} />;
}

export function LiveMapPage() {
  const { slug } = useParams<{ slug: string }>();
  const mapEl = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layers = useRef<Map<string, Layer>>(new Map());
  const courseLayerRef = useRef<CourseLayer | null>(null);
  const fitted = useRef(false);
  const fittedCourse = useRef(false);

  const live = useQuery({
    queryKey: ['track-live', slug],
    queryFn: () => api.trackLive(slug!, 5),
    enabled: !!slug,
    refetchInterval: 4000,
  });
  const course = useQuery({
    queryKey: ['course', slug],
    queryFn: () => api.raceCourse(slug!),
    enabled: !!slug,
    refetchInterval: 10000,
  });
  // Klokka: starttid og status fra det lette klokke-endepunktet (fanger komiteens
  // varselsignal og utsettelse raskt), klasseflagg og påmeldte fra regattaen.
  const klokke = useQuery({
    queryKey: ['race-clock', slug],
    queryFn: () => api.raceClock(slug!),
    enabled: !!slug,
    refetchInterval: 5000,
  });
  const raceInfo = useQuery({
    queryKey: ['race', slug],
    queryFn: () => api.race(slug!),
    enabled: !!slug,
    refetchInterval: 30_000,
  });
  const finish = useQuery({
    queryKey: ['race-finish', slug],
    queryFn: () => api.finishList(slug!),
    enabled: !!slug,
    refetchInterval: 10_000,
  });

  // Tidssynk mot serveren ved lasting og hvert 10. minutt.
  const [offsetMs, setOffsetMs] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const sync = async () => {
      const r = await syncTime(5);
      if (!cancelled) setOffsetMs(r.offsetMs);
    };
    void sync();
    const id = setInterval(() => void sync(), 10 * 60_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  const finishes = useMemo(() => maalgangerPerSeil(finish.data?.records), [finish.data]);
  const lop = useMemo(() => lopRader(course.data), [course.data]);
  const klasseflagg = useMemo(() => {
    const flagg = [...new Set((raceInfo.data?.classes ?? []).map((c) => c.flag))];
    return flagg.length ? flagg : ['G'];
  }, [raceInfo.data]);

  // Siste båts tid når alle påmeldte er ferdige (i mål eller med merknad), eller når
  // regattaen er satt til ferdig.
  const iMaalMs = useMemo(() => {
    let last: number | null = null;
    for (const f of finishes.values()) last = Math.max(last ?? 0, f.elapsedMs);
    if (last == null) return null;
    if (klokke.data?.status === 'finished') return last;
    const entries = raceInfo.data?.entries ?? [];
    const ferdige = ferdigeSeil(finish.data?.records);
    return entries.length > 0 && entries.every((e) => ferdige.has(sailKey(e.sailNumber))) ? last : null;
  }, [finishes, finish.data, raceInfo.data, klokke.data]);

  // Ingen felles klokke ved egen startklokke, før komiteen har startet, eller ved avlysning.
  const k = klokke.data;
  const visKlokke =
    !!k &&
    k.status !== 'cancelled' &&
    k.startMode !== 'manual' &&
    !(k.startMode === 'committee' && !k.committeeStarted && k.status !== 'postponed');

  // Init kart én gang
  useEffect(() => {
    if (!mapEl.current || mapRef.current) return;
    const el = mapEl.current;
    const map = L.map(el, { zoomControl: true }).setView(DEFAULT_CENTER, 12);

    const graatone = L.tileLayer(
      'https://cache.kartverket.no/v1/wmts/1.0.0/topograatone/default/webmercator/{z}/{y}/{x}.png',
      { maxZoom: 18, attribution: KV_ATTR },
    );
    const sjokart = L.tileLayer(
      'https://cache.kartverket.no/v1/wmts/1.0.0/sjokartraster/default/webmercator/{z}/{y}/{x}.png',
      { maxZoom: 18, attribution: KV_ATTR },
    );
    const topo = L.tileLayer(
      'https://cache.kartverket.no/v1/wmts/1.0.0/topo/default/webmercator/{z}/{y}/{x}.png',
      { maxZoom: 18, attribution: KV_ATTR },
    );
    const osm = L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap',
    });

    // Standard: rolig gråtone-kart (ren fjord). Sjøkart m/ dybder som valg.
    graatone.addTo(map);
    L.control.layers(
      { Gråtone: graatone, Sjøkart: sjokart, Topografisk: topo, OpenStreetMap: osm },
      {},
      { collapsed: true },
    ).addTo(map);

    mapRef.current = map;
    courseLayerRef.current = createCourseLayer(map);
    // Oppdater kartstørrelsen når fanen får dimensjoner (ny bakgrunns-fane på mobil)
    const ro = new ResizeObserver(() => map.invalidateSize());
    ro.observe(el);
    const t = setTimeout(() => map.invalidateSize(), 100);

    return () => {
      clearTimeout(t);
      ro.disconnect();
      map.remove();
      mapRef.current = null;
      courseLayerRef.current = null;
      layers.current.clear();
      fitted.current = false;
      fittedCourse.current = false;
    };
  }, []);

  // Oppdater markører/spor når data endres
  useEffect(() => {
    const map = mapRef.current;
    const data = live.data;
    if (!map || !data) return;

    const seen = new Set<string>();
    for (const boat of data.boats) {
      seen.add(boat.sailNumber);
      const color = colorForSail(boat.sailNumber);
      const latlngs = boat.trail.map((p) => [p.lat, p.lon] as [number, number]);
      const lastPos: [number, number] = [boat.last.lat, boat.last.lon];

      let layer = layers.current.get(boat.sailNumber);
      if (!layer) {
        const marker = L.marker(lastPos, { icon: boatIcon(boat.sailNumber, color) }).addTo(map);
        const line = L.polyline(latlngs, { color, weight: 3, opacity: 0.75 }).addTo(map);
        layer = { marker, line };
        layers.current.set(boat.sailNumber, layer);
      } else {
        layer.marker.setLatLng(lastPos);
        layer.line.setLatLngs(latlngs);
      }
    }

    // Fjern båter som ikke lenger er aktive
    for (const [sail, layer] of layers.current) {
      if (!seen.has(sail)) {
        map.removeLayer(layer.marker);
        map.removeLayer(layer.line);
        layers.current.delete(sail);
      }
    }

    // Sentrer på flåten første gang det finnes båter (vent til fanen har størrelse)
    if (!fitted.current && data.boats.length > 0 && mapEl.current && mapEl.current.clientHeight > 1) {
      const pts = data.boats.map((b) => [b.last.lat, b.last.lon] as [number, number]);
      map.invalidateSize();
      map.fitBounds(L.latLngBounds(pts).pad(0.3), { maxZoom: 15 });
      fitted.current = true;
    }
  }, [live.data]);

  // Tegn bane (merker + start-/mållinje) når geometrien oppdateres
  useEffect(() => {
    courseLayerRef.current?.render(course.data);
  }, [course.data]);

  // Ramme inn banen når ingen båter sender posisjon ennå (så løypa vises før start)
  useEffect(() => {
    const map = mapRef.current;
    const el = mapEl.current;
    if (!map || !el || el.clientHeight <= 1) return;
    if (fitted.current || fittedCourse.current) return;
    if ((live.data?.boats.length ?? 0) > 0) return; // båter → la båt-innrammingen styre
    const pts: [number, number][] = [];
    for (const m of course.data?.marks ?? []) pts.push([m.lat, m.lon]);
    for (const p of course.data?.startLine ?? []) pts.push([p.lat, p.lon]);
    for (const p of course.data?.finishLine ?? []) pts.push([p.lat, p.lon]);
    if (pts.length === 0) return;
    map.invalidateSize();
    map.fitBounds(L.latLngBounds(pts).pad(0.3), { maxZoom: 15 });
    fittedCourse.current = true;
  }, [course.data, live.data]);

  const boats = live.data?.boats ?? [];
  const raceName = live.data?.race.name ?? slug;

  return (
    <div
      className="fixed inset-x-0 top-0 z-20 h-screen flex flex-col bg-slate-950"
      style={{ height: '100dvh' }}
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-800 z-[1000]">
        <div className="flex items-center gap-3 min-w-0">
          <Link to={`/r/${slug}`} className="text-slate-400 text-sm shrink-0">&larr;</Link>
          <h1 className="font-semibold truncate">🗺️ {raceName}</h1>
          <span className="text-xs text-slate-500 shrink-0">{boats.length} båter</span>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <Link to={`/spor/${slug}`} className="text-xs text-sky-400">📡 Send pos.</Link>
          <Link to={`/kart/${slug}/reprise`} className="text-xs text-sky-400">⏮ Reprise</Link>
        </div>
      </div>

      <div className="flex-1 relative">
        <div ref={mapEl} className="absolute inset-0" />

        {/* Båtliste-overlay: båtene i mål først med plass og tid, resten med fart */}
        {boats.length > 0 && (
          <div className="absolute top-2 right-2 z-[1000] bg-slate-900/85 border border-slate-700 rounded-lg p-2 max-h-[60%] overflow-auto text-xs">
            {sorterMedMaal(
              boats.map((b) => ({ b, sailNumber: b.sailNumber, maal: finishes.get(sailKey(b.sailNumber)) ?? null })),
            ).map(({ b, maal }) => (
              <div key={b.sailNumber} className="flex items-center gap-2 py-0.5">
                <span
                  className="w-3 h-3 rounded-full border border-white/70 shrink-0"
                  style={{ background: colorForSail(b.sailNumber) }}
                />
                <span className="font-mono">{b.sailNumber}</span>
                {maal ? (
                  <span className="ml-auto pl-3 font-mono tabular-nums text-emerald-300">
                    {maal.position != null && `${maal.position}. `}
                    {formatElapsed(maal.elapsedMs)}
                  </span>
                ) : (
                  <span className="text-slate-400 tabular-nums ml-auto">
                    {b.last.sogKn != null ? `${b.last.sogKn.toFixed(1)} kn` : ''}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        {(lop.length > 0 || visKlokke) && (
          <div className="absolute left-2 bottom-2 z-[1000] pointer-events-none flex flex-col items-start gap-2">
            {lop.length > 0 && <LopListe rader={lop} />}
            {visKlokke && k && (
              <LiveKlokke
                startMs={new Date(k.startTime).getTime()}
                offsetMs={offsetMs}
                status={k.status}
                iMaalMs={iMaalMs}
                klasseflagg={klasseflagg}
              />
            )}
          </div>
        )}

        {boats.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="bg-slate-900/85 border border-slate-700 rounded-xl px-4 py-3 text-center text-sm text-slate-300">
              Ingen båter sender posisjon enda.
              <div className="text-xs text-slate-500 mt-1">
                Åpne «📡 Send pos.» på båtene for å starte sporing.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
